import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, MenuItem, Typography, Alert, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import { CustomTextfield } from '@/components/CustomTextfield/styles';
import { getSupportSchema, SupportSchema, BrowserInfo } from '../schema';
import { getBrowserInfo, getBrowserSummary } from './BrowserDetection';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { versionService } from '@/services/versionService';
import { useAuthStore } from '@/stores/authStore';
import { checkExtensionInstalled, requestExtensionDiagnosticData } from '@/utils/extensionDiagnostics';
import { errorReporter, isFirebaseOfflineError } from '@/services/errorReportingService';

const ISSUE_TYPES = [
  { value: 'Bug Report', label: 'Bug Report' },
  { value: 'Feature Request', label: 'Feature Request' },
  { value: 'Account Issue', label: 'Account Issue' },
  { value: 'Billing Question', label: 'Billing Question' },
  { value: 'Technical Support', label: 'Technical Support' },
  { value: 'Other', label: 'Other' },
];

interface SupportFormProps {
  onSubmitSuccess?: () => void;
}

const SupportForm = ({ onSubmitSuccess }: SupportFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCollectingDiagnostics, setIsCollectingDiagnostics] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [submitWarning, setSubmitWarning] = useState<string | null>(null);
  const [extensionDetected, setExtensionDetected] = useState(false);
  const [includeExtensionDiagnostics, setIncludeExtensionDiagnostics] = useState(false);
  const browserInfo = useMemo<BrowserInfo>(() => getBrowserInfo(), []);

  const { currentUser, userData, userLoggedIn } = useAuthStore();

  // Prioritize userData from backend, fallback to Firebase user info
  const userInfo = useMemo(() => {
    if (!userLoggedIn || !currentUser) {
      return { name: '', email: '' };
    }

    const firstName = userData?.first_name || '';
    const lastName = userData?.last_name || '';
    const fullName =
      firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || currentUser.displayName || '';

    const email = userData?.email || currentUser.email || '';

    return { name: fullName, email };
  }, [userLoggedIn, currentUser, userData]);

  const schema = getSupportSchema({
    required: 'This field is required',
    invalidEmail: 'Please enter a valid email address',
  });

  useEffect(() => {
    let cancelled = false;

    const detectExtension = async () => {
      try {
        const detected = await checkExtensionInstalled();
        if (!cancelled) {
          setExtensionDetected(detected);
          setIncludeExtensionDiagnostics(detected);
        }
      } catch (error) {
        errorReporter.expected('Failed to detect Chrome extension', error, {
          feature: 'extension',
          operation: 'detectExtension',
        });
      }
    };

    detectExtension();

    return () => {
      cancelled = true;
    };
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SupportSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: userInfo.name,
      email: userInfo.email,
      issueType: '',
      comment: '',
    },
  });

  const onSubmit = async (data: SupportSchema) => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitWarning(null);

    try {
      const versionInfo = versionService.getVersionInfo();

      // Filter out undefined values from versionInfo to prevent Firebase errors
      const cleanVersionInfo = Object.fromEntries(
        Object.entries(versionInfo).filter(([_, value]) => value !== undefined),
      );

      let extensionDiagnosticData = null;

      if (extensionDetected && includeExtensionDiagnostics) {
        setIsCollectingDiagnostics(true);
        extensionDiagnosticData = await requestExtensionDiagnosticData();
        setIsCollectingDiagnostics(false);

        if (!extensionDiagnosticData) {
          setSubmitWarning(
            'Chrome extension diagnostic data could not be retrieved. We submitted your report without it.',
          );
        }
      }

      const diagnosticDataIncluded = extensionDiagnosticData !== null;

      const supportTicket = {
        ...data,
        browserInfo,
        browserSummary: getBrowserSummary(browserInfo),
        versionInfo: cleanVersionInfo,
        subscriptionInfo: userData?.subscription || null,
        userInfo: {
          userId: userData?.id || null,
          firebaseUid: currentUser?.uid || null,
          email: userData?.email || currentUser?.email || data.email,
          firstName: userData?.first_name || null,
          lastName: userData?.last_name || null,
        },
        diagnosticData: extensionDiagnosticData,
        extensionDiagnosticDataIncluded: diagnosticDataIncluded,
        extensionDiagnosticsRequested: includeExtensionDiagnostics,
        extensionDetected,
        createdAt: new Date(),
        status: 'Open',
      };

      await addDoc(collection(db, 'support_tickets'), supportTicket);

      // Log to Sentry only after successful submission — no PII in extra
      errorReporter.captureMessage('Support ticket submitted', 'info', {
        feature: 'support',
        operation: 'onSubmit',
        issueType: data.issueType,
        extensionDetected: String(extensionDetected),
        extensionDiagnosticDataIncluded: String(diagnosticDataIncluded),
      });

      setSubmitStatus('success');
      reset({
        name: userInfo.name,
        email: userInfo.email,
        issueType: '',
        comment: '',
      });
      onSubmitSuccess?.();
    } catch (error) {
      if (isFirebaseOfflineError(error)) {
        errorReporter.expected('Support ticket submission failed (client offline)', error, {
          feature: 'firebase',
          operation: 'onSubmit',
        });
      } else {
        errorReporter.critical('Support ticket submission failed', error, {
          feature: 'support',
          operation: 'onSubmit',
        });
      }
      setSubmitStatus('error');
    } finally {
      setIsCollectingDiagnostics(false);
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Contact Support
      </Typography>

      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Having an issue? We're here to help! Please fill out the form below and we'll get back to you as soon as
        possible.
      </Typography>

      {submitStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Your support request has been submitted successfully. We'll get back to you soon!
        </Alert>
      )}

      {submitStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          There was an error submitting your request. Please try again. If the problem persists, you can reach us at
          support@notego.io.
        </Alert>
      )}

      {submitWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {submitWarning}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Show name field only if user is not logged in or has no name */}
          {(!userLoggedIn || !userInfo.name) && (
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  fullWidth
                  label="Your Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isSubmitting}
                  size="small"
                />
              )}
            />
          )}

          {/* Show email field only if user is not logged in or has no email */}
          {(!userLoggedIn || !userInfo.email) && (
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  fullWidth
                  label="Email Address"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isSubmitting}
                  size="small"
                />
              )}
            />
          )}

          {/* Show user info when fields are hidden */}
          {userLoggedIn && userInfo.name && userInfo.email && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                <strong>Submitting as:</strong> {userInfo.name} ({userInfo.email})
              </Typography>
            </Box>
          )}

          <Controller
            name="issueType"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                fullWidth
                select
                label="Issue Type"
                error={!!errors.issueType}
                helperText={errors.issueType?.message}
                disabled={isSubmitting}
                size="small"
              >
                {ISSUE_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </CustomTextfield>
            )}
          />

          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                fullWidth
                multiline
                rows={3}
                label="Describe your issue"
                placeholder="Please provide as much detail as possible about your issue..."
                error={!!errors.comment}
                helperText={errors.comment?.message}
                disabled={isSubmitting}
                size="small"
                inputProps={{
                  'data-gramm': 'false',
                  'data-gramm_editor': 'false',
                  'data-enable-grammarly': 'false',
                }}
                sx={{
                  '& .MuiInputBase-inputMultiline': {
                    resize: 'vertical',
                  },
                }}
              />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={extensionDetected && includeExtensionDiagnostics}
                onChange={(event) => setIncludeExtensionDiagnostics(event.target.checked)}
                disabled={!extensionDetected || isSubmitting}
              />
            }
            label={
              extensionDetected
                ? 'Include Chrome extension diagnostic data (helps us fix issues faster)'
                : 'Chrome extension not detected'
            }
            sx={{
              color: 'text.secondary',
              '& .MuiFormControlLabel-label': { fontSize: '14px' },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            size="medium"
            disabled={isSubmitting}
            sx={{
              mt: 2,
              borderRadius: '100px',
              py: 1,
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {isCollectingDiagnostics ? 'Collecting extension diagnostics...' : 'Submitting...'}
              </>
            ) : (
              'Submit Support Request'
            )}
          </Button>

          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ mb: 1, color: 'text.secondary', display: 'block' }}>
              <strong>Browser Info:</strong> {getBrowserSummary(browserInfo)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              This information helps us troubleshoot your issue more effectively.
            </Typography>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default SupportForm;
